import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, KeyRound, Loader2, ShieldCheck, CheckCircle, AlertCircle, Check, ArrowLeft } from 'lucide-react';
import client from '../../src/api/client';
import '../styles/admin-base.css';
import "../styles/login.css";

const PASSWORD_REQUIREMENTS = [
  { label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { label: 'Uppercase letter (A-Z)', test: (v) => /[A-Z]/.test(v) },
  { label: 'Lowercase letter (a-z)', test: (v) => /[a-z]/.test(v) },
  { label: 'Special character (!@#$%^&*)', test: (v) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(v) },
];

export default function AdminResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  // Step: 'verify_totp' | 'new_password' | 'done'
  const [step, setStep] = useState('verify_totp');

  // TOTP verification state
  const [totpCode, setTotpCode] = useState('');
  const [isBackupCode, setIsBackupCode] = useState(false);
  const [resetToken, setResetToken] = useState('');

  // Password state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // General state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reqResults = useMemo(
    () => PASSWORD_REQUIREMENTS.map((r) => ({ ...r, met: r.test(password) })),
    [password],
  );
  const allMet = reqResults.every((r) => r.met);

  const handleTotpCodeChange = (e) => {
    const val = e.target.value;
    if (isBackupCode) {
      if (/^[a-fA-F0-9]{0,8}$/.test(val)) setTotpCode(val);
    } else {
      if (/^\d{0,6}$/.test(val)) setTotpCode(val);
    }
  };

  // Step 1: Verify reset token + TOTP
  const handleVerifyTotp = async (e) => {
    e.preventDefault();
    if (!token || !email) {
      setError('Invalid reset link. Please request a new one.');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const response = await client.post('/api/admin/auth/verify-reset', {
        token,
        email,
        totpCode: totpCode.trim(),
        isBackupCode,
      });
      setResetToken(response.data.resetToken);
      setStep('new_password');
    } catch (err) {
      setError(err.response?.data?.message || 'Verification failed.');
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Set new password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!allMet) {
      setError('Please meet all password requirements.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      await client.post('/api/admin/auth/reset-password', {
        resetToken,
        password,
      });
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.message || 'Password reset failed.');
    } finally {
      setLoading(false);
    }
  };

  // ── Success view ──
  if (step === 'done') {
    return (
      <div className="admin-login-container">
        <div className="admin-login-card">
          <div className="admin-login-header">
            <div className="admin-logo-circle admin-logo-circle--success">
              <CheckCircle size={32} color="#16a34a" />
            </div>
            <h1>Password Reset Complete</h1>
            <p>Your password has been updated and all previous sessions have been terminated.</p>
          </div>

          <div className="admin-login-form">
            <Link
              to="/admin/login"
              className="admin-button is-primary is-full-width admin-button-link"
            >
              Go to Login
            </Link>
          </div>

          <div className="admin-login-footer">
            <p>Union Local 305 &copy; {new Date().getFullYear()}</p>
          </div>
        </div>
      </div>
    );
  }

  // ── TOTP Verification Step ──
  if (step === 'verify_totp') {
    return (
      <div className="admin-login-container">
        <div className="admin-login-card">
          <div className="admin-login-header">
            <div className="admin-logo-circle">
              <KeyRound size={32} color="#2563eb" />
            </div>
            <h1>Verify Your Identity</h1>
            <p>
              {isBackupCode
                ? 'Enter one of your backup codes to continue'
                : 'Enter the 6-digit code from your authenticator app'}
            </p>
          </div>

          <form onSubmit={handleVerifyTotp} className="admin-login-form">
            {error && (
              <div className="admin-alert is-error admin-alert--with-icon">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            <div className="admin-form-group">
              <label htmlFor="totp-code">
                {isBackupCode ? 'Backup Code' : 'Authenticator Code'}
              </label>
              <div className="admin-totp-input">
                <input
                  type="text"
                  id="totp-code"
                  value={totpCode}
                  onChange={handleTotpCodeChange}
                  placeholder={isBackupCode ? 'Enter backup code' : '000000'}
                  maxLength={isBackupCode ? 8 : 6}
                  autoComplete="one-time-code"
                  inputMode={isBackupCode ? 'text' : 'numeric'}
                  autoFocus
                />
              </div>
            </div>

            <button
              type="submit"
              className="admin-button is-primary is-full-width"
              disabled={loading || (isBackupCode ? totpCode.length < 1 : totpCode.length !== 6)}
            >
              {loading ? <Loader2 size={18} className="admin-spin" /> : 'Verify & Continue'}
            </button>

            <div className="admin-totp-toggle">
              <button
                type="button"
                onClick={() => {
                  setIsBackupCode(!isBackupCode);
                  setTotpCode('');
                  setError(null);
                }}
              >
                {isBackupCode ? 'Use authenticator code instead' : 'Use a backup code instead'}
              </button>
            </div>

            <div className="admin-login-footer">
              <Link
                to="/admin/forgot-password"
                className="admin-link-muted admin-link-back"
              >
                <ArrowLeft size={14} /> Request new link
              </Link>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ── New Password Step ──
  if (step === 'new_password') {
    return (
      <div className="admin-login-container">
        <div className="admin-login-card">
          <div className="admin-login-header">
            <div className="admin-logo-circle">
              <Lock size={32} color="#2563eb" />
            </div>
            <h1>Create New Password</h1>
            <p>Enter your new password below</p>
          </div>

          <form onSubmit={handleResetPassword} className="admin-login-form">
            {error && (
              <div className="admin-alert is-error admin-alert--with-icon">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            <div className="admin-form-group">
              <label htmlFor="new-password">New Password</label>
              <div className="admin-input-wrapper">
                <Lock size={18} className="admin-input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="admin-input"
                  autoFocus
                />
                <button
                  type="button"
                  className="admin-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="admin-password-req-list">
              {reqResults.map((r, i) => (
                <div key={i} className={`admin-password-req-item ${r.met ? 'is-met' : ''}`}>
                  <Check size={14} className="admin-password-req-icon" />
                  {r.label}
                </div>
              ))}
            </div>

            <div className="admin-form-group">
              <label htmlFor="confirm-password">Confirm Password</label>
              <div className={`admin-input-wrapper ${confirmPassword && password !== confirmPassword ? 'is-error' : ''}`}>
                <Lock size={18} className="admin-input-icon" />
                <input
                  type={showConfirm ? 'text' : 'password'}
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="admin-input"
                />
                <button
                  type="button"
                  className="admin-password-toggle"
                  onClick={() => setShowConfirm(!showConfirm)}
                >
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="admin-button is-primary is-full-width"
              disabled={loading || !allMet || password !== confirmPassword}
            >
              {loading ? <Loader2 size={18} className="admin-spin" /> : 'Reset Password'}
            </button>
          </form>

          <div className="admin-login-footer">
            <p>Union Local 305 &copy; {new Date().getFullYear()}</p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
